/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useStatusOverlay } from '@fs/zion-ui'
import { baseUrl } from '@fs/zion-config'
import {
  removeFromLocalStorage,
  updateCachedImageArray,
  getFromLocalStorage,
} from '../views/imageQualityAudit/utilities/utilities'
import createNewAuditObject from '../views/imageQualityAudit/utilities/createNewAuditObject'
import { useDetermineMode, useGroupData } from '../views/imageBrowse/hooks'
import transl from '../views/imageBrowse/groupData/internalFields/components/qualityTags/QualityTags.transl'
import qualityIssuesTransl from '../views/imageQualityAudit/imageQualityAudit.transl'
import fsOperationsCallHeader from '../utils/fsOperationsCallHeader'
import { useSetFocusedIndex } from '../components/groupImageBrowser/hooks'
import { useSharedBrowserStateContext } from '../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

const auditKeys = [
  'defective',
  'duplicate',
  'extraMaterialInFrame',
  'improperlyCropped',
  'informationObscured',
  'missingPages',
  'motionBlurOrOutOfFocus',
  'multiple',
  'overUnderExposed',
  'reflections',
  'wrongCaptureMode',
  'wrongImageOrientation',
  'other',
]

const ImageAuditContext = createContext({})

const auditMutation = gql`
  mutation updateImageQualityAudit($imageAudits: [ImageInfo], $rmsId: String, $projectId: String) {
    updateImageQualityAudit(imageAudits: $imageAudits, rmsId: $rmsId, projectId: $projectId) {
      code
      success
      message
    }
  }
`
const TimeoutTime = 1000

const findTags = (p) => p?.name === 'folderQTags' && p?.type === 'GROUP'
const findComments = (p) =>
  (p?.name === 'recordCaptureComment' && p?.type === 'GROUP') || (p?.name === 'comments' && p?.type === 'GROUP')

/**
 * @param {*} imageQualityMode added to help with tests
 */
const ImageAuditContextProvider = ({ children, imageQualityMode = false }) => {
  const sharedBrowserState = useSharedBrowserStateContext()
  const { group, loading, initialChildId } = useGroupData()
  const [submitImageAuditUpdate] = useMutation(auditMutation)
  const showStatusOverlay = useStatusOverlay()
  const { showImageQualityAudit } = useDetermineMode()

  const [audit, setAudit] = useState(null)
  const [stickOnImage, setStickOnImage] = useState(false)

  const rmsId = group?.id

  const qualityIssuesList = auditKeys.map((k) => ({
    key: k,
    primaryText: qualityIssuesTransl[k](),
  }))

  const qualityTags = useMemo(() => {
    if (group?.properties) {
      const tags = group.properties.find(findTags)
      const split = tags?.values?.[0]?.split(';').filter(Boolean) || []
      if (split.length > 0) return split.map((v) => transl[`qualityTags${v}`]())
    }

    return []
  }, [group])

  const qualityComments = useMemo(() => {
    if (group?.properties) {
      const comments = group.properties.find(findComments)
      if (comments) return comments.values.join('\n')
    }

    return null
  }, [group])

  const originalAuditCount = useMemo(() => audit?.filter((img) => img.optional !== true).length, [audit])

  const setFocusedIndex = useSetFocusedIndex(false)
  const setFocusIndex = useCallback(
    (idx) => {
      setFocusedIndex(idx - 1)
    },
    [setFocusedIndex]
  )

  const getFullAudit = useCallback(async () => {
    if (audit) return audit
    const data = await getFromLocalStorage(group.id)
    return data.imageInfo
  }, [audit, group])

  const getCurrentImageAudit = useCallback(async () => {
    if (!imageQualityMode && !showImageQualityAudit) return []
    let currentAudit = []
    if (!audit) {
      const storedData = await getFromLocalStorage(group.id)
      currentAudit = storedData?.imageInfo || []
    } else currentAudit = [...audit]

    if (sharedBrowserState.focusedIndex < 0) return { idx: 0, currentImageAudit: currentAudit[0] }
    const imageAudit = currentAudit?.find((i) => i.imageNumber === sharedBrowserState.focusedIndex + 1)
    const idx = currentAudit?.indexOf(imageAudit)

    return { idx, imageAudit }
  }, [sharedBrowserState.focusedIndex, audit, group])

  const currentImageInfo = useMemo(() => {
    if (!audit || sharedBrowserState.focusedIndex < 0) return audit?.[0] || { auditIssues: [] }
    const imageAudit = audit.find((i) => i.imageNumber === sharedBrowserState.focusedIndex + 1)
    return imageAudit || { auditIssues: [] }
  }, [audit, sharedBrowserState.focusedIndex])

  const updateAuditState = useCallback(
    async (value, key = 'auditIssues', idx = -1) => {
      const currentAudit = [...audit]
      let updateIdx = idx
      if (updateIdx === -1) {
        const currentImageAudit = audit.find((i) => i.imageNumber === sharedBrowserState.focusedIndex + 1)
        updateIdx = currentAudit.indexOf(currentImageAudit)
      }
      currentAudit[updateIdx][key] = value

      setAudit(currentAudit)
      updateCachedImageArray(group.id, currentAudit)
    },
    [getFullAudit, sharedBrowserState.focusedIndex, group]
  )

  useEffect(() => {
    async function setImageLoaded() {
      const { imageAudit } = await getCurrentImageAudit()
      if (imageAudit && !imageAudit?.loaded) updateAuditState(true, 'loaded')
    }

    setImageLoaded()
  }, [getCurrentImageAudit, updateAuditState])

  const auditComplete = useMemo(() => {
    if (!audit) return false
    const length = audit?.length || 0
    if (length === 1) return true

    const unloadedImages = audit.find((image) => !image.loaded)
    if (unloadedImages) return false

    const allViewed = audit?.reduce((acc, current) => {
      return acc || (current.viewed && current.imageNumber !== sharedBrowserState.focusedIndex + 1)
    }, false)

    return allViewed && length > 0
  }, [audit, sharedBrowserState.focusedIndex])

  useEffect(() => {
    async function loadOrCreateAudit() {
      const auditIsLoaded = group?.childCount && !loading && (group.id === initialChildId || !initialChildId)
      if (auditIsLoaded) {
        const savedData = await getFromLocalStorage(group.id)
        let dataToSet = savedData
        if (!dataToSet) dataToSet = createNewAuditObject(group)
        setAudit(dataToSet.imageInfo)
      }
    }

    loadOrCreateAudit()
  }, [group, loading, initialChildId])

  useEffect(() => {
    function createOptionalImageReview() {
      if (imageQualityMode || showImageQualityAudit) {
        let currentAudit = [...audit]
        let update = false

        const isInList = currentAudit.find((item) => item.imageNumber === sharedBrowserState.focusedIndex + 1)
        if (isInList) {
          if (!isInList.viewed) {
            setStickOnImage(true)
            setTimeout(() => setStickOnImage(false), TimeoutTime)
            update = true
          }
          isInList.viewed = true
        } else if (sharedBrowserState.focusedIndex > -1) {
          currentAudit.push({
            auditIssues: [],
            optional: true,
            imageNumber: sharedBrowserState.focusedIndex + 1,
            userAdded: false,
            viewed: true,
            loaded: true,
          })

          currentAudit = currentAudit.sort((a, b) => {
            if (parseInt(a.imageNumber, 10) > parseInt(b.imageNumber, 10)) return 1
            if (parseInt(a.imageNumber, 10) < parseInt(b.imageNumber, 10)) return -1
            return 0
          })

          update = true
        }

        if (update) {
          setAudit(currentAudit)
          updateCachedImageArray(group.id, currentAudit)
        }
      }
    }
    if (audit) createOptionalImageReview()
  }, [audit, sharedBrowserState.focusedIndex, group, showImageQualityAudit, imageQualityMode])

  async function submitAudit() {
    try {
      const filteredAudits = audit.filter((i) => i?.auditIssues?.length > 0)
      const imageAudits = filteredAudits.map((image) => ({
        ...image,
        apid: group.imageApids[image.imageNumber - 1],
      }))

      const metisProjectId = group?.properties?.find((property) => property.name === 'metisProjectId')?.values?.[0]
      const response = await submitImageAuditUpdate({
        variables: { imageAudits, rmsId, projectId: metisProjectId },
        context: { headers: { ...fsOperationsCallHeader } },
      })
      if (!response?.data?.updateImageQualityAudit?.success) throw response

      removeFromLocalStorage(group.id)

      const storedAuditId = localStorage.getItem('react-audit-quality')
      const groupProjectIdProperty = group.properties.find((p) => ['metisProjectId', 'projectNumber'].includes(p.name))
      const projectId = storedAuditId?.projectId || groupProjectIdProperty?.values?.[0]

      const isDevelopmentEnv = process.env.NODE_ENV === 'development'
      const urlEndString = projectId ? `/${projectId}?auditCompletedFor=${group.id}` : ''
      const url = isDevelopmentEnv ? 'http://localhost:5006' : `${baseUrl}/records`

      window.location.assign(`${url}/audit${urlEndString}`)
    } catch (error) {
      showStatusOverlay({
        message: 'There was an error submitting the audit. Please try again.',
        type: 'error',
      })
      console.error(error)
    }
  }

  const updateImageIssues = useCallback(
    (newList) => {
      updateAuditState(newList)
    },
    [updateAuditState]
  )

  const nextImage = useCallback(() => {
    const { focusedIndex } = sharedBrowserState
    const nextAudit = audit.find((a) => a.imageNumber - 1 > focusedIndex)
    if (nextAudit) {
      const { imageNumber, viewed = false } = nextAudit
      setFocusIndex(imageNumber)
      if (!viewed) {
        setStickOnImage(true)
        setTimeout(() => setStickOnImage(false), TimeoutTime)
      }
    }
  }, [sharedBrowserState.focusedIndex, audit, setFocusIndex])

  const previousImage = useCallback(() => {
    const { focusedIndex } = sharedBrowserState
    const nextAudit = audit.findLast((a) => a.imageNumber - 1 < focusedIndex)
    if (nextAudit) {
      const { imageNumber, viewed = false } = nextAudit
      setFocusIndex(imageNumber)
      if (!viewed) {
        setStickOnImage(true)
        setTimeout(() => setStickOnImage(false), TimeoutTime)
      }
    }
  }, [sharedBrowserState.focusedIndex, audit, setFocusIndex])

  const focusedIndex = useMemo(() => sharedBrowserState.focusedIndex, [sharedBrowserState.focusedIndex])

  const contextValue = useMemo(
    () => ({
      audit,
      auditComplete,
      auditKeys,
      currentImageInfo,
      focusedIndex,
      getCurrentImageAudit,
      group,
      loading,
      originalAuditCount,
      nextImage,
      previousImage,
      qualityIssuesList,
      qualityTags,
      qualityComments,
      setFocusIndex,
      submitAudit,
      stickOnImage,
      updateImageIssues,
    }),
    [
      audit,
      auditComplete,
      auditKeys,
      currentImageInfo,
      focusedIndex,
      getCurrentImageAudit,
      group,
      loading,
      originalAuditCount,
      nextImage,
      previousImage,
      qualityIssuesList,
      qualityTags,
      qualityComments,
      setFocusIndex,
      submitAudit,
      stickOnImage,
      updateImageIssues,
    ]
  )

  return <ImageAuditContext.Provider value={contextValue}>{children}</ImageAuditContext.Provider>
}

export const useImageAuditContext = () => useContext(ImageAuditContext)
export default ImageAuditContextProvider
